'use client'

import { INode } from '@/api/nodes/mutations/directory/create-directory-mutation'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { run } from 'shared'
import { compact } from '@/utils/functions/compact'
import { useDeleteNodesMutation } from '@/api/nodes/mutations/delete/delete-nodes-mutation'
import { toast } from '@/features/toast/toast'
import { buildNodePath, cleanNodePath } from '@/features/drive/entities/directory/directory-context'
import { QueryKey } from '@tanstack/react-query'
import { useDeleteDirectoryContentMutation } from '@/api/nodes/mutations/delete/delete-directory-content-mutation'
import { useMoveNodesMutation } from '@/api/nodes/mutations/move/move-nodes-mutation'
import { useMoveDirectoryContentMutation } from '@/api/nodes/mutations/move/move-directory-content-mutation'
import { useDownloadFiles } from '@/features/files/download-files'

interface IUserDirectoryContextContentSelection {
    directory: INode | null | undefined
    content: INode[] | undefined
    nodePath: string
    nodeContentQueryKey: QueryKey
    maxSelectCount?: number
}

export type ISelectionMode = 'directory' | 'individual'

interface ISelectedIndicesState {
    list: string[] // UUIDs
    record: Record<string, true> // UUID -> true
    directoryPath: string | null | undefined
    lastClickedUuid: string | null // Track the last clicked UUID for shift-selection
}

export const useDirectorySelectionManager = ({
    content,
    directory,
    nodePath,
    nodeContentQueryKey,
    maxSelectCount,
}: IUserDirectoryContextContentSelection) => {
    const {
        selectedDirectoryPath,
        setSelectedUuids,
        toggleUuid,
        selectedIndicesState,
        selectionMode,
        selectedCount,
        selectedNodes,
        clearSelection,
        setSelectedDirectoryPath,
        freezeForPotentialReset,
        getIsSelected,
    } = useDirectoryContextSelectionProcess({ content, directory, path: nodePath })

    const { downloadSelected } = useDirectoryContextSelectionDownload({ content, selectedIndicesState })

    const { deleteSelected, isDeleteSelectedOpen, openDeleteSelectedDialog, closeDeleteSelectedDialog } =
        useDirectoryContextSelectionDelete({
            nodeContentQueryKey,
            selectionMode,
            selectedDirectoryPath,
            selectedCount,
            selectedIndicesState,
            content,
            path: nodePath,
            clearSelection,
            freezeForPotentialReset,
        })

    const { moveSelected } = useDirectoryContextSelectionMove({
        nodeContentQueryKey,
        selectionMode,
        selectedCount,
        selectedDirectoryPath,
        selectedNodes,
        clearSelection,
        freezeForPotentialReset,
    })

    return {
        state: {
            selectedNodes,
            selectionMode,
            selectedCount,
            selectedUuids: selectedIndicesState.list,
            selectedDirectoryPath,
            maxSelectCount,
        },

        methods: {
            setSelectedDirectoryPath,
            getIsSelected,
            setSelectedUuids,
            toggleUuid,
            clearSelection,
            freezeForPotentialReset,
        },

        move: {
            moveSelected,
        },

        deletion: {
            deleteSelected,
            isDeleteSelectedOpen,
            openDeleteSelectedDialog,
            closeDeleteSelectedDialog,
        },

        download: {
            downloadSelected,
        },
    }
}

// --- SELECTION PROCESS ---

interface IUseDirectoryContextSelectionProcessParams {
    content: INode[] | undefined
    directory: INode | null | undefined
    path: string
}

const useDirectoryContextSelectionProcess = ({
    content,
    directory,
    path,
}: IUseDirectoryContextSelectionProcessParams) => {
    // null = root directory, undefined = no directory selected
    const [selectedDirectoryPath, rawSetSelectedDirectoryPath] = useState<string | null>(null)

    const [selectedIndicesState, rawSetSelectedIndicesState] = useState<ISelectedIndicesState>({
        list: [],
        record: {},
        directoryPath: undefined,
        lastClickedUuid: null,
    })

    const getIndexFromUuid = useCallback(
        (uuid: string) => content?.findIndex((node) => node.uuid === uuid) ?? -1,
        [content]
    )

    const getUuidFromIndex = useCallback((index: number) => content?.[index]?.uuid, [content])

    const selectionMode = useMemo<ISelectionMode | null>(
        () =>
            selectedDirectoryPath && selectedDirectoryPath === path
                ? 'directory'
                : selectedIndicesState.list.length
                  ? 'individual'
                  : null,
        [selectedDirectoryPath, selectedIndicesState.list, path]
    )

    const selectedCount = useMemo(
        () =>
            selectionMode === 'directory'
                ? (directory?.numberOfItems ?? selectedIndicesState.list.length)
                : selectedIndicesState.list.length,
        [selectionMode, directory, selectedIndicesState.list]
    )

    const setSelectedUuids = useCallback(
        (uuids: string[], lastClickedUuid?: string) => {
            if (!content) return

            const nextRecord = uuids.reduce<Record<string, true>>((acc, uuid) => {
                acc[uuid] = true
                return acc
            }, {})

            rawSetSelectedIndicesState({
                directoryPath: path,
                list: uuids,
                record: nextRecord,
                lastClickedUuid: lastClickedUuid ?? null,
            })
        },
        [content, path]
    )

    const toggleUuid = useCallback(
        (uuid: string, nextIsSelected: boolean, group?: boolean, selectAll?: boolean) => {
            // If deselecting and directory mode is active, exit directory mode
            if (!nextIsSelected && selectedDirectoryPath) {
                rawSetSelectedDirectoryPath(null)
            }

            // Deselection: just remove the UUID
            if (!nextIsSelected) {
                const nextUuids = selectedIndicesState.list.filter((u) => u !== uuid)

                setSelectedUuids(nextUuids, selectedIndicesState.lastClickedUuid ?? undefined)

                return
            }

            if (selectAll) {
                const allUuids = content?.map((node) => node.uuid) ?? []

                setSelectedUuids(allUuids, uuid)

                return
            }

            if (!group) {
                const nextUuids = [...selectedIndicesState.list, uuid]

                setSelectedUuids(nextUuids, uuid)

                return
            }

            // Shift-selection: select range from last clicked to current
            const anchorUuid = selectedIndicesState.lastClickedUuid

            // No previous click, just select this one
            if (!anchorUuid) {
                const nextUuids = [...selectedIndicesState.list, uuid]

                setSelectedUuids(nextUuids, uuid)

                return
            }

            const currentIndex = getIndexFromUuid(uuid)
            const anchorIndex = getIndexFromUuid(anchorUuid)

            // One of the items no longer exists, fallback to single selection
            if (anchorIndex === -1 || currentIndex === -1) {
                const nextUuids = [...selectedIndicesState.list, uuid]

                setSelectedUuids(nextUuids, uuid)

                return
            }

            const start = Math.min(anchorIndex, currentIndex)
            const end = Math.max(anchorIndex, currentIndex)

            const rangeUuids = Array.from({ length: end - start + 1 }, (_, i) => i + start)
                .map((i) => getUuidFromIndex(i))
                .filter(Boolean) as string[]

            const nextUuids = Array.from(new Set([...selectedIndicesState.list, ...rangeUuids]))

            setSelectedUuids(nextUuids, uuid)
        },
        [
            setSelectedUuids,
            content,
            selectedDirectoryPath,
            selectedIndicesState.list,
            selectedIndicesState.lastClickedUuid,
            getIndexFromUuid,
            getUuidFromIndex,
        ]
    )

    const selectedNodes = useMemo(
        () => compact(selectedIndicesState.list.map((uuid) => content?.find((node) => node.uuid === uuid))),
        [selectedIndicesState.list, content]
    )

    const clearSelection = useCallback(() => {
        rawSetSelectedIndicesState({ list: [], record: {}, directoryPath: path, lastClickedUuid: null })
        rawSetSelectedDirectoryPath(null)
    }, [path])

    const setSelectedDirectoryPath = useCallback(
        (nextPath: string | null) => {
            const contentLength = content?.length ?? 0

            if (!nextPath) {
                const areAllSelected = selectedIndicesState.list.length === contentLength

                if (areAllSelected) clearSelection()

                rawSetSelectedDirectoryPath(null)

                return
            }

            if (!selectedDirectoryPath || selectedDirectoryPath === nextPath) {
                const firstUuid = content?.[0]?.uuid
                if (firstUuid) {
                    toggleUuid(firstUuid, true, true, true)
                }
            }

            rawSetSelectedDirectoryPath(nextPath)
        },
        [content, selectedDirectoryPath, selectedIndicesState.list, clearSelection, toggleUuid]
    )

    const freezeForPotentialReset = useCallback(() => {
        const currSelectedUuids = selectedIndicesState.list
        const currLastClickedUuid = selectedIndicesState.lastClickedUuid
        const currSelectedDirectoryUuid = selectedDirectoryPath

        return () => {
            setSelectedUuids(currSelectedUuids, currLastClickedUuid ?? undefined)
            rawSetSelectedDirectoryPath(currSelectedDirectoryUuid)
        }
    }, [selectedIndicesState.list, selectedIndicesState.lastClickedUuid, selectedDirectoryPath, setSelectedUuids])

    const getIsSelected = useCallback(
        (uuid: string, directoryPath: string) => {
            return (
                (selectedDirectoryPath && cleanNodePath(directoryPath) === cleanNodePath(selectedDirectoryPath)) ||
                selectedIndicesState.record[uuid] === true
            )
        },
        [selectedIndicesState.record, selectedDirectoryPath]
    )

    /**
     * If the directory changes, we need to clear the selection
     */
    useEffect(() => {
        if (!selectedDirectoryPath) return

        // Path must have changed
        if (selectedDirectoryPath !== path) {
            clearSelection()
        }
    }, [path, selectedDirectoryPath, clearSelection])

    /**
     * Clear selection for nodes that no longer exist in content
     */
    useEffect(() => {
        if (!content || selectedIndicesState.list.length === 0) return

        const contentUuids = new Set(content.map((node) => node.uuid))
        const stillExistingUuids = selectedIndicesState.list.filter((uuid) => contentUuids.has(uuid))

        if (stillExistingUuids.length !== selectedIndicesState.list.length) {
            setSelectedUuids(stillExistingUuids, selectedIndicesState.lastClickedUuid ?? undefined)
        }
    }, [content, selectedIndicesState.list, selectedIndicesState.lastClickedUuid, setSelectedUuids])

    return {
        clearSelection,
        selectedDirectoryPath,
        setSelectedUuids,
        toggleUuid,
        selectedIndicesState,
        selectionMode,
        selectedCount,
        selectedNodes,
        setSelectedDirectoryPath,
        freezeForPotentialReset,
        getIsSelected,
    }
}

// --- MOVE ---

interface IUseMoveNodesMutationParams {
    nodeContentQueryKey: QueryKey
    selectionMode: ISelectionMode | null
    selectedCount: number
    selectedDirectoryPath: string | null
    selectedNodes: INode[] | undefined
    clearSelection: () => void
    freezeForPotentialReset: () => () => void
}

const useDirectoryContextSelectionMove = ({
    nodeContentQueryKey,
    selectionMode,
    selectedCount,
    selectedDirectoryPath,
    clearSelection,
    freezeForPotentialReset,
    selectedNodes,
}: IUseMoveNodesMutationParams) => {
    const { mutateAsync: moveNodesAsync } = useMoveNodesMutation({
        nodeContentQueryKey,
        sendToastSuccess: (count) => toast.success(`Successfully moved ${count} ${count === 1 ? 'item' : 'items'}`),
        sendToastError: (count) => toast.error(`An error occurred while moving the ${count === 1 ? 'item' : 'items'}`),
    })

    const { mutateAsync: moveDirectoryContentAsync } = useMoveDirectoryContentMutation({
        nodeContentQueryKey,
        sendToastSuccess: (count) => toast.success(`Successfully moved ${count} ${count === 1 ? 'item' : 'items'}`),
        sendToastError: (count) => toast.error(`An error occurred while moving the ${count === 1 ? 'item' : 'items'}`),
    })

    const moveSelected = useCallback(
        (params: { newDirectoryPath: string }) => {
            const resetSelection = freezeForPotentialReset()

            const promise = run(() => {
                switch (selectionMode) {
                    case 'directory':
                        return selectedDirectoryPath
                            ? moveDirectoryContentAsync({
                                  selectedCount,
                                  selectedDirectoryPath,
                                  newDirectoryPath: params.newDirectoryPath,
                              })
                            : undefined

                    case 'individual':
                        return selectedNodes?.length
                            ? moveNodesAsync({
                                  paths: selectedNodes.map((node) => node.path),
                                  newDirectoryPath: params.newDirectoryPath,
                              })
                            : undefined
                }
            })

            if (!promise) return

            // Optimistic update
            clearSelection()

            return promise.catch(() => {
                resetSelection()

                throw new Error('An error occurred while moving the items')
            })
        },
        [
            clearSelection,
            freezeForPotentialReset,
            moveDirectoryContentAsync,
            moveNodesAsync,
            selectedDirectoryPath,
            selectedNodes,
            selectionMode,
            selectedCount,
        ]
    )

    return { moveSelected }
}

// --- DELETE ---

interface IUseDeleteNodesMutationParams {
    nodeContentQueryKey: QueryKey
    selectionMode: ISelectionMode | null
    selectedDirectoryPath: string | null
    selectedCount: number
    selectedIndicesState: ISelectedIndicesState
    content: INode[] | undefined
    path: string
    freezeForPotentialReset: () => () => void
    clearSelection: () => void
}

const useDirectoryContextSelectionDelete = ({
    nodeContentQueryKey,
    selectionMode,
    selectedDirectoryPath,
    selectedCount,
    selectedIndicesState,
    content,
    path,
    freezeForPotentialReset,
    clearSelection,
}: IUseDeleteNodesMutationParams) => {
    const [isDeleteSelectedOpen, setIsDeleteSelectedOpen] = useState(false)

    const { mutateAsync: deleteNodesAsync } = useDeleteNodesMutation({
        nodeContentQueryKey,
        sendToastSuccess: (count) => toast.success(`Successfully deleted ${count} ${count > 1 ? 'items' : 'item'}`),
        sendToastError: (count) => toast.error(`An error occurred while deleting the ${count > 1 ? 'items' : 'item'}`),
    })

    const { mutateAsync: deleteDirectoryAsync } = useDeleteDirectoryContentMutation({
        nodeContentQueryKey,
        sendToastSuccess: (count) => toast.success(`Successfully deleted ${count} ${count > 1 ? 'items' : 'item'}`),
        sendToastError: (count) => toast.error(`An error occurred while deleting the ${count > 1 ? 'items' : 'item'}`),
    })

    const deleteSelected = useCallback(() => {
        const resetSelection = freezeForPotentialReset()

        const promise = run(() => {
            switch (selectionMode) {
                case 'directory':
                    return selectedDirectoryPath
                        ? deleteDirectoryAsync({ selectedCount, selectedDirectoryPath: selectedDirectoryPath })
                        : undefined

                case 'individual': {
                    const paths = compact(
                        selectedIndicesState.list.map((uuid) => {
                            const node = content?.find((n) => n.uuid === uuid)

                            if (!node) return null

                            return buildNodePath(path, node.slug)
                        })
                    )

                    return paths?.length ? deleteNodesAsync({ paths }) : undefined
                }
            }
        })

        if (!promise) return

        // Optimistic update
        clearSelection()

        return promise.catch((e) => {
            resetSelection()

            throw e
        })
    }, [
        clearSelection,
        freezeForPotentialReset,
        content,
        deleteDirectoryAsync,
        deleteNodesAsync,
        path,
        selectedDirectoryPath,
        selectedIndicesState.list,
        selectionMode,
        selectedCount,
    ])

    const openDeleteSelectedDialog = useMemo(() => {
        if (!selectedCount) return null

        return () => setIsDeleteSelectedOpen(true)
    }, [selectedCount])

    const closeDeleteSelectedDialog = useCallback(() => {
        setIsDeleteSelectedOpen(false)
    }, [])

    return { deleteSelected, isDeleteSelectedOpen, openDeleteSelectedDialog, closeDeleteSelectedDialog }
}

// --- DOWNLOAD ---

interface IUseDirectoryContextSelectionDownloadParams {
    content: INode[] | undefined
    selectedIndicesState: ISelectedIndicesState
}

const useDirectoryContextSelectionDownload = ({
    content,
    selectedIndicesState,
}: IUseDirectoryContextSelectionDownloadParams) => {
    const downloadFiles = useDownloadFiles()

    const downloadSelected = useMemo(() => {
        if (!selectedIndicesState.list.length) return null

        return async () => {
            const selectedFiles = compact(
                selectedIndicesState.list.map((uuid) => {
                    const node = content?.find((n) => n.uuid === uuid)

                    if (!node?.file?.url) return null

                    return node.file
                })
            )

            return downloadFiles({ files: selectedFiles })
        }
    }, [selectedIndicesState.list, content, downloadFiles])

    return { downloadSelected }
}
